import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RotateCcw, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

const categoryLabels = {
  full_payment: "Full Payment Commitment",
  installment: "Instalment Plan",
  flexible: "Flexible Check-In",
  no_contact: "No Contact",
};

function OptionRow({ checked, onChange, label, description, warn, disabled }) {
  return (
    <label
      className={cn(
        "flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors",
        disabled
          ? "opacity-50 cursor-not-allowed bg-muted/30"
          : checked
            ? "bg-primary/5 border-primary/30"
            : "hover:bg-muted/40 border-border/50",
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => !disabled && onChange(e.target.checked)}
        className="mt-0.5 h-3.5 w-3.5 accent-primary shrink-0"
      />
      <div className="flex-1 min-w-0 space-y-0.5">
        <p className="text-xs font-semibold leading-tight">{label}</p>
        <p className="text-[10px] text-muted-foreground leading-snug">
          {description}
        </p>
        {warn && (
          <p className="text-[10px] text-amber-600 font-medium flex items-center gap-1 mt-1">
            <AlertTriangle className="h-3 w-3 shrink-0" />
            {warn}
          </p>
        )}
      </div>
    </label>
  );
}

/**
 * Props:
 *   log       — FollowUpLog document (from getHistory)
 *   sponsor   — Sponsorship document (from queue, has followUp with instalmentPlan / paymentCommitment)
 *   isOpen    — boolean
 *   onClose   — fn()
 *   onConfirm — fn({ logId, clearCommitment, clearPlan, resetFollowUpStatus, decrementFailedAttempts })
 *   isLoading — boolean
 */
export function DeleteLogModal({
  log,
  sponsor,
  isOpen,
  onClose,
  onConfirm,
  isLoading,
}) {
  const followUp = sponsor?.followUp || {};
  const logId = log?._id;

  // Which data did this log create?
  const commitmentOwnedByLog =
    followUp.paymentCommitment?.createdByLogId?.toString() ===
    logId?.toString();
  const planOwnedByLog =
    followUp.instalmentPlan?.createdByLogId?.toString() === logId?.toString();

  const commitmentFulfilled =
    followUp.paymentCommitment?.status === "fulfilled";
  const planHasPaid =
    planOwnedByLog &&
    (followUp.instalmentPlan?.instalments || []).some(
      (i) => i.status === "paid" || i.status === "partial",
    );
  const planPaidCount = planOwnedByLog
    ? (followUp.instalmentPlan?.instalments || []).filter(
        (i) => i.status === "paid" || i.status === "partial",
      ).length
    : 0;

  const isNoContact = log?.dispositionCategory === "no_contact";
  const currentFailedAttempts = followUp.failedAttempts || 0;

  // Default options
  const [opts, setOpts] = useState({
    resetFollowUpStatus: true,
    clearCommitment: false,
    clearPlan: false,
    decrementFailedAttempts: isNoContact && currentFailedAttempts > 0,
  });

  if (!log) return null;

  const set = (key) => (val) => setOpts((o) => ({ ...o, [key]: val }));

  const handleConfirm = () => {
    onConfirm({
      logId,
      clearCommitment:
        opts.clearCommitment && commitmentOwnedByLog && !commitmentFulfilled,
      clearPlan: opts.clearPlan && planOwnedByLog && !planHasPaid,
      resetFollowUpStatus: opts.resetFollowUpStatus,
      decrementFailedAttempts: opts.decrementFailedAttempts && isNoContact,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="h-4 w-4" />
            Delete Log
          </DialogTitle>
          <DialogDescription>
            <span className="font-medium text-foreground">
              {categoryLabels[log.dispositionCategory] ||
                log.dispositionCategory}
            </span>{" "}
            logged on{" "}
            {new Date(log.createdAt).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 mt-1">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground px-1">
            What to undo
          </p>

          {/* Always shown: reset follow-up status */}
          <OptionRow
            checked={opts.resetFollowUpStatus}
            onChange={set("resetFollowUpStatus")}
            label="Reset follow-up status"
            description="Roll back the status and next-action date to the previous log's state (or to 'Pending First Call' if this was the first log)."
          />

          {/* no_contact: undo failed attempt */}
          {isNoContact && (
            <OptionRow
              checked={opts.decrementFailedAttempts}
              onChange={set("decrementFailedAttempts")}
              disabled={currentFailedAttempts === 0}
              label="Undo failed attempt count"
              description={`Currently at ${currentFailedAttempts} failed attempt${currentFailedAttempts !== 1 ? "s" : ""}. Decrements by 1.`}
            />
          )}

          {/* full_payment: clear commitment */}
          {commitmentOwnedByLog && (
            <OptionRow
              checked={opts.clearCommitment && !commitmentFulfilled}
              onChange={set("clearCommitment")}
              disabled={commitmentFulfilled}
              label="Clear payment commitment"
              description="Remove the payment commitment card this log created."
              warn={
                commitmentFulfilled
                  ? "This commitment has been fulfilled — delete the payment first."
                  : undefined
              }
            />
          )}

          {/* installment: clear plan */}
          {planOwnedByLog && (
            <OptionRow
              checked={opts.clearPlan && !planHasPaid}
              onChange={set("clearPlan")}
              disabled={planHasPaid}
              label="Clear instalment plan"
              description="Remove the full instalment schedule this log created."
              warn={
                planHasPaid
                  ? `${planPaidCount} instalment${planPaidCount !== 1 ? "s" : ""} already paid — delete those payments first.`
                  : undefined
              }
            />
          )}
        </div>

        <div className="flex gap-2 justify-end mt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={handleConfirm}
            disabled={isLoading}
            className="flex items-center gap-1.5"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            {isLoading ? "Deleting…" : "Delete &amp; Apply"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
